import {
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../firebase";

export type SerialMovementType =
  | "مبيعات"
  | "مردودات مبيعات";

export interface ExcelSerialRow {
  serialNumber: string;
  productId: string;
  points: number;
  movementType: SerialMovementType;
  rowNumber?: number;
}

export type SerialProcessStatus =
  | "success_moved"
  | "success_not_found"
  | "already_used"
  | "already_in_user"
  | "product_mismatch"
  | "invalid"
  | "failed"
  | "return_success"
  | "return_not_found"
  | "return_wrong_user"

  // Undo statuses
  | "undo_restored"
  | "undo_removed"
  | "undo_used_not_found"
  | "undo_wrong_user"
  | "undo_user_serial_not_found"
  | "undo_source_conflict";

export interface SerialProcessResult {
  serialNumber: string;
  productId: string;
  points: number;
  movementType?: SerialMovementType;
  rowNumber?: number;
  status: SerialProcessStatus;
  message: string;
  existedInSerailNumber?: boolean;
}

export interface AssignSerialsSummary {
  total: number;
  processed: number;

  success: number;
  moved: number;
  notFound: number;

  alreadyUsed: number;
  alreadyInUser: number;
  productMismatch: number;

  returnSuccess: number;
  returnNotFound: number;
  returnWrongUser: number;

  invalid: number;
  failed: number;

  addedPoints: number;
  removedPoints: number;
}

export interface UndoSerialsSummary {
  total: number;
  processed: number;

  success: number;

  restored: number;
  removed: number;

  usedNotFound: number;
  wrongUser: number;
  userSerialNotFound: number;
  sourceConflict: number;

  failed: number;

  removedPoints: number;
}

interface UndoSerialsOptions {
  onProgress?: (
    current: number,
    total: number,
    result: SerialProcessResult
  ) => void;
}

interface AssignSerialsOptions {
  note?: string;
  onProgress?: (
    current: number,
    total: number,
    result: SerialProcessResult
  ) => void;
}

const USERS_COLLECTION = "users";
const ACTIVE_SERIALS_COLLECTION = "SerailNumber";
const USED_SERIALS_COLLECTION = "UsedSerialNumbers";

const normalizeText = (value: unknown): string => {
  return String(value ?? "").trim();
};

const normalizeSerialNumber = (value: unknown): string => {
  return normalizeText(value).replace(/\s+/g, "");
};

const normalizePoints = (value: unknown): number => {
  if (typeof value === "number") {
    return value;
  }

  const normalizedValue = String(value ?? "")
    .trim()
    .replace(/,/g, "");

  return Number(normalizedValue);
};

export const validateSerialRow = (
  row: ExcelSerialRow
): string | null => {
  const serialNumber = normalizeSerialNumber(row.serialNumber);
  const productId = normalizeText(row.productId);
  const points = normalizePoints(row.points);

  const movementType = normalizeText(
  row.movementType
);

  if (!serialNumber) {
    return "رقم السيريال غير موجود";
  }

  if (serialNumber.includes("/")) {
    return "رقم السيريال يحتوي على الرمز / ولا يمكن استخدامه كمعرف دكيومنت";
  }

  if (!productId) {
    return "معرف المنتج productId غير موجود";
  }

  if (!Number.isFinite(points)) {
    return "قيمة النقاط غير صحيحة";
  }

  if (points <= 0) {
    return "عدد النقاط يجب أن يكون أكبر من صفر";
  }

  if (
  movementType !== "مبيعات" &&
  movementType !== "مردودات مبيعات"
) {
  return "نوع الحركة يجب أن يكون مبيعات أو مردودات مبيعات";
}

  return null;
};

const processSalesReturn = async (
  userId: string,
  row: ExcelSerialRow
): Promise<SerialProcessResult> => {
  const serialNumber =
    normalizeSerialNumber(row.serialNumber);

  const excelProductId =
    normalizeText(row.productId);

  if (!serialNumber) {
    return {
      serialNumber: "",
      productId: excelProductId,
      points: 0,
      movementType: "مردودات مبيعات",
      rowNumber: row.rowNumber,
      status: "invalid",
      message: "رقم السيريال غير موجود",
    };
  }

  if (serialNumber.includes("/")) {
    return {
      serialNumber,
      productId: excelProductId,
      points: 0,
      movementType: "مردودات مبيعات",
      rowNumber: row.rowNumber,
      status: "invalid",
      message:
        "رقم السيريال يحتوي على / ولا يمكن استخدامه كمعرف دكيومنت",
    };
  }

  try {
    const transactionResult =
      await runTransaction(
        db,
        async (transaction) => {
          const userRef = doc(
            db,
            USERS_COLLECTION,
            userId
          );

          const usedSerialRef = doc(
            db,
            USED_SERIALS_COLLECTION,
            serialNumber
          );

          /*
           * أولًا نقرأ المستخدم و UsedSerialNumbers.
           */
          const userSnapshot =
            await transaction.get(userRef);

          const usedSerialSnapshot =
            await transaction.get(
              usedSerialRef
            );

          if (!userSnapshot.exists()) {
            throw new Error(
              "USER_NOT_FOUND"
            );
          }

          /*
           * إذا كان السيريال موجودًا في UsedSerialNumbers
           * نستخدم productId الحقيقي المخزن فيه.
           */
          const usedData =
            usedSerialSnapshot.exists()
              ? usedSerialSnapshot.data()
              : null;

          /*
           * حماية:
           * إذا كان UsedSerialNumbers يقول إن السيريال
           * يعود لمستخدم آخر فلا نحذفه.
           */
          if (
            usedData &&
            normalizeText(usedData.userId) &&
            normalizeText(
              usedData.userId
            ) !== userId
          ) {
            return {
              status:
                "return_wrong_user" as const,

              message:
                "السيريال يعود لمستخدم آخر ولم يتم تنفيذ مردود المبيعات",

              productId:
                normalizeText(
                  usedData.productId
                ) || excelProductId,

              points:
                normalizePoints(
                  usedData.points
                ) || 0,
            };
          }

          const productId =
            normalizeText(
              usedData?.productId
            ) || excelProductId;

          if (!productId) {
            throw new Error(
              "RETURN_PRODUCT_ID_MISSING"
            );
          }

          const userSerialRef = doc(
            db,
            USERS_COLLECTION,
            userId,
            "myPoints",
            productId,
            "myserail",
            serialNumber
          );

          /*
           * قراءة السيريال الفعلي الموجود لدى المستخدم.
           */
          const userSerialSnapshot =
            await transaction.get(
              userSerialRef
            );

          /*
           * أهم شرط:
           * إذا لم يكن موجودًا لدى المستخدم
           * لا نحذف ولا نخصم ولا نضيف أي شيء.
           */
          if (
            !userSerialSnapshot.exists()
          ) {
            return {
              status:
                "return_not_found" as const,

              message:
                "السيريال غير موجود لدى المستخدم، لم يتم تنفيذ مردود المبيعات",

              productId,
              points: 0,
            };
          }

          const userSerialData =
            userSerialSnapshot.data();

          /*
           * نأخذ النقاط الحقيقية المخزنة مع السيريال.
           * وإذا لم تكن موجودة نستخدم UsedSerialNumbers.
           *
           * لا نعتمد على قيمة النقاط الموجودة في Excel
           * عند الخصم.
           */
          const actualPoints =
            normalizePoints(
              userSerialData.points
            ) ||
            normalizePoints(
              usedData?.points
            );

          if (
            !Number.isFinite(
              actualPoints
            ) ||
            actualPoints <= 0
          ) {
            throw new Error(
              "RETURN_POINTS_INVALID"
            );
          }

          const currentUserPoints =
            Number(
              userSnapshot.data().points
            ) || 0;

          /*
           * حماية من وصول النقاط إلى قيمة سالبة.
           */
          if (
            currentUserPoints <
            actualPoints
          ) {
            throw new Error(
              "USER_POINTS_LESS_THAN_RETURN_POINTS"
            );
          }

          const updatedUserPoints =
            currentUserPoints -
            actualPoints;

          /*
           * حذف السيريال من المستخدم.
           */
          transaction.delete(
            userSerialRef
          );

          /*
           * إذا كان موجودًا في UsedSerialNumbers
           * نحذفه أيضًا لأنه لم يعد مستخدمًا لدى المستخدم.
           */
          if (
            usedSerialSnapshot.exists()
          ) {
            transaction.delete(
              usedSerialRef
            );
          }

          /*
           * خصم النقاط الأصلية للسيريال.
           */
          transaction.update(
            userRef,
            {
              points:
                updatedUserPoints,

              pointsUpdatedAt:
                serverTimestamp(),
            }
          );

          return {
            status:
              "return_success" as const,

            message:
              "تم تنفيذ مردود المبيعات: حذف السيريال من المستخدم وخصم نقاطه",

            productId,
            points: actualPoints,
          };
        }
      );

    return {
      serialNumber,

      productId:
        transactionResult.productId ||
        excelProductId,

      points:
        Number(
          transactionResult.points
        ) || 0,

      movementType:
        "مردودات مبيعات",

      rowNumber: row.rowNumber,

      status:
        transactionResult.status,

      message:
        transactionResult.message,
    };
  } catch (error) {
    console.error(
      `خطأ أثناء تنفيذ مردود المبيعات للسيريال ${serialNumber}:`,
      error
    );

    let message =
      error instanceof Error
        ? error.message
        : "حدث خطأ غير معروف";

    switch (message) {
      case "USER_NOT_FOUND":
        message =
          "المستخدم غير موجود";
        break;

      case "RETURN_PRODUCT_ID_MISSING":
        message =
          "تعذر تحديد productId للسيريال";
        break;

      case "RETURN_POINTS_INVALID":
        message =
          "قيمة نقاط السيريال المخزنة غير صحيحة";
        break;

      case "USER_POINTS_LESS_THAN_RETURN_POINTS":
        message =
          "نقاط المستخدم الحالية أقل من نقاط السيريال، تم إيقاف مردود المبيعات لحماية البيانات";
        break;
    }

    return {
      serialNumber,
      productId: excelProductId,
      points: 0,
      movementType:
        "مردودات مبيعات",
      rowNumber: row.rowNumber,
      status: "failed",
      message,
    };
  }
};

const processSingleSerial = async (
  userId: string,
  row: ExcelSerialRow,
  note: string
): Promise<SerialProcessResult> => {

  if (
  row.movementType ===
  "مردودات مبيعات"
) {
  return processSalesReturn(
    userId,
    row
  );
}

  const serialNumber = normalizeSerialNumber(row.serialNumber);
  const productId = normalizeText(row.productId);
  const points = normalizePoints(row.points);

  const validationError = validateSerialRow({
    ...row,
    serialNumber,
    productId,
    points,
  });

  if (validationError) {
    return {
      serialNumber,
      productId,
      points: Number.isFinite(points) ? points : 0,
      rowNumber: row.rowNumber,
      status: "invalid",
      message: validationError,
    };
  }

  try {
    const transactionResult = await runTransaction(
      db,
      async (transaction) => {
        const userRef = doc(
          db,
          USERS_COLLECTION,
          userId
        );

        const sourceSerialRef = doc(
          db,
          ACTIVE_SERIALS_COLLECTION,
          serialNumber
        );

        const usedSerialRef = doc(
          db,
          USED_SERIALS_COLLECTION,
          serialNumber
        );

        const productPointsRef = doc(
          db,
          USERS_COLLECTION,
          userId,
          "myPoints",
          productId
        );

        const userSerialRef = doc(
          db,
          USERS_COLLECTION,
          userId,
          "myPoints",
          productId,
          "myserail",
          serialNumber
        );

        /*
         * جميع عمليات القراءة يجب أن تحدث قبل
         * أي عملية كتابة داخل Firestore Transaction.
         */
        const userSnapshot = await transaction.get(userRef);
        const usedSerialSnapshot =
          await transaction.get(usedSerialRef);
        const userSerialSnapshot =
          await transaction.get(userSerialRef);
        const sourceSerialSnapshot =
          await transaction.get(sourceSerialRef);

        if (!userSnapshot.exists()) {
          throw new Error("USER_NOT_FOUND");
        }

        /*
         * إذا كان السيريال موجودًا في الكولكشن المركزي
         * للسيريلات المستخدمة، فلا نضيف النقاط مرة أخرى.
         */
        if (usedSerialSnapshot.exists()) {
          const usedData = usedSerialSnapshot.data();

          return {
            status: "already_used" as const,
            message:
              usedData.userId === userId
                ? "السيريال مستخدم مسبقًا من قبل نفس المستخدم"
                : "السيريال مستخدم مسبقًا من قبل مستخدم آخر",
            existedInSerailNumber:
              sourceSerialSnapshot.exists(),
          };
        }

        /*
         * حماية إضافية في حال كان السيريال موجودًا
         * داخل حساب المستخدم، لكنه غير موجود في
         * UsedSerialNumbers بسبب بيانات قديمة.
         */
        if (userSerialSnapshot.exists()) {
          return {
            status: "already_in_user" as const,
            message:
              "السيريال موجود مسبقًا داخل نقاط المستخدم",
            existedInSerailNumber:
              sourceSerialSnapshot.exists(),
          };
        }

        const sourceSerialData =
          sourceSerialSnapshot.exists()
            ? sourceSerialSnapshot.data()
            : null;

        const sourceProductId = normalizeText(
          sourceSerialData?.productId
        );

        /*
         * عند وجود السيريال في SerailNumber،
         * نتحقق من أن productId الموجود في Excel
         * يطابق productId المخزن في Firebase.
         */
        if (
          sourceSerialSnapshot.exists() &&
          sourceProductId &&
          sourceProductId !== productId
        ) {
          return {
            status: "product_mismatch" as const,
            message:
              `معرف المنتج في Excel لا يطابق معرف المنتج في Firebase. ` +
              `Excel: ${productId} - Firebase: ${sourceProductId}`,
            existedInSerailNumber: true,
          };
        }

        const currentUserPoints =
          Number(userSnapshot.data().points) || 0;

        const updatedUserPoints =
          currentUserPoints + points;

        /*
         * إنشاء دكيومنت المنتج داخل myPoints.
         * نستخدم productId كمعرف للدكيومنت.
         */
        transaction.set(
          productPointsRef,
          {
            productId,
            updatedAt: serverTimestamp(),
          },
          {
            merge: true,
          }
        );

        /*
         * إضافة السيريال إلى حساب المستخدم.
         * رقم السيريال نفسه هو Document ID.
         */
        transaction.set(userSerialRef, {
          serialNumber,
          productId,
          points,
          note,
          dateuploaded: serverTimestamp(),
          source: "dashboard_excel",
        });

        const originalData: Record<string, unknown> =
  sourceSerialData
    ? { ...sourceSerialData }
    : {};

        /*
         * إنشاء السيريال داخل UsedSerialNumbers.
         */
        transaction.set(usedSerialRef, {
          serialNumber,
          userId,
          productId,
          points,
          note,
          source: "dashboard_excel",
          existedInSerailNumber:
            sourceSerialSnapshot.exists(),
          originalData,
          transferredAt: serverTimestamp(),
        });

        /*
         * حذف السيريال من SerailNumber فقط
         * عندما يكون موجودًا.
         */
        if (sourceSerialSnapshot.exists()) {
          transaction.delete(sourceSerialRef);
        }

        /*
         * تحديث مجموع نقاط المستخدم.
         */
        transaction.update(userRef, {
          points: updatedUserPoints,
          pointsUpdatedAt: serverTimestamp(),
        });

        return {
          status: sourceSerialSnapshot.exists()
            ? ("success_moved" as const)
            : ("success_not_found" as const),

          message: sourceSerialSnapshot.exists()
            ? "تمت إضافة النقاط ونقل السيريال بنجاح"
            : "السيريال غير موجود في SerailNumber، وتمت إضافته للمستخدم وUsedSerialNumbers",

          existedInSerailNumber:
            sourceSerialSnapshot.exists(),
        };
      }
    );

   return {
  serialNumber,
  productId,
  points,
  movementType: "مبيعات",
  rowNumber: row.rowNumber,
  status: transactionResult.status,
  message: transactionResult.message,
  existedInSerailNumber:
    transactionResult.existedInSerailNumber,
};
  } catch (error) {
    console.error(
      `خطأ أثناء معالجة السيريال ${serialNumber}:`,
      error
    );

    const message =
      error instanceof Error
        ? error.message
        : "حدث خطأ غير معروف";

    return {
      serialNumber,
      productId,
      points,
      rowNumber: row.rowNumber,
      status: "failed",
      message:
        message === "USER_NOT_FOUND"
          ? "المستخدم غير موجود"
          : message,
    };
  }
};

export const createEmptySummary =
  (): AssignSerialsSummary => ({
    total: 0,
    processed: 0,

    success: 0,
    moved: 0,
    notFound: 0,

    alreadyUsed: 0,
    alreadyInUser: 0,
    productMismatch: 0,

    returnSuccess: 0,
    returnNotFound: 0,
    returnWrongUser: 0,

    invalid: 0,
    failed: 0,

    addedPoints: 0,
    removedPoints: 0,
  });

const updateSummary = (
  summary: AssignSerialsSummary,
  result: SerialProcessResult
): AssignSerialsSummary => {
  const updatedSummary = {
    ...summary,
    processed: summary.processed + 1,
  };

  switch (result.status) {
    case "success_moved":
      updatedSummary.success += 1;
      updatedSummary.moved += 1;
      updatedSummary.addedPoints += result.points;
      break;

    case "success_not_found":
      updatedSummary.success += 1;
      updatedSummary.notFound += 1;
      updatedSummary.addedPoints += result.points;
      break;

    case "already_used":
      updatedSummary.alreadyUsed += 1;
      break;

    case "already_in_user":
      updatedSummary.alreadyInUser += 1;
      break;

      case "return_success":
  updatedSummary.returnSuccess += 1;
  updatedSummary.removedPoints +=
    result.points;
  break;

case "return_not_found":
  updatedSummary.returnNotFound += 1;
  break;

case "return_wrong_user":
  updatedSummary.returnWrongUser += 1;
  break;

    case "product_mismatch":
      updatedSummary.productMismatch += 1;
      break;

    case "invalid":
      updatedSummary.invalid += 1;
      break;

    case "failed":
      updatedSummary.failed += 1;
      break;
  }

  return updatedSummary;
};

export const checkUserExists = async (
  userId: string
): Promise<boolean> => {
  const normalizedUserId = normalizeText(userId);

  if (!normalizedUserId) {
    return false;
  }

  const userRef = doc(
    db,
    USERS_COLLECTION,
    normalizedUserId
  );

  const userSnapshot = await getDoc(userRef);

  return userSnapshot.exists();
};

export const assignSerialsToUser = async (
  userId: string,
  rows: ExcelSerialRow[],
  options: AssignSerialsOptions = {}
): Promise<{
  results: SerialProcessResult[];
  summary: AssignSerialsSummary;
}> => {
  const normalizedUserId = normalizeText(userId);

  if (!normalizedUserId) {
    throw new Error("يرجى إدخال معرف المستخدم");
  }

  if (!rows.length) {
    throw new Error("لا توجد سيريلات للمعالجة");
  }

  const userExists =
    await checkUserExists(normalizedUserId);

  if (!userExists) {
    throw new Error(
      "معرف المستخدم غير موجود داخل كولكشن users"
    );
  }

  const defaultNote =
    options.note?.trim() ||
    "تمت إضافة السيريال من ملف Excel بواسطة لوحة التحكم";

  const results: SerialProcessResult[] = [];

  let summary: AssignSerialsSummary = {
    ...createEmptySummary(),
    total: rows.length,
  };

  /*
   * تتم المعالجة بصورة متسلسلة؛ لأن جميع السيريلات
   * تحدث حقل points لنفس المستخدم.
   *
   * تشغيل 600 Transaction في الوقت نفسه قد يسبب
   * تعارضات كثيرة على دكيومنت المستخدم.
   */
  for (let index = 0; index < rows.length; index++) {
    const row = rows[index];

    const result = await processSingleSerial(
      normalizedUserId,
      row,
      defaultNote
    );

    results.push(result);

    summary = updateSummary(summary, result);

    options.onProgress?.(
      index + 1,
      rows.length,
      result
    );
  }

  return {
    results,
    summary,
  };
};

const createEmptyUndoSummary = (): UndoSerialsSummary => ({
  total: 0,
  processed: 0,

  success: 0,

  restored: 0,
  removed: 0,

  usedNotFound: 0,
  wrongUser: 0,
  userSerialNotFound: 0,
  sourceConflict: 0,

  failed: 0,

  removedPoints: 0,
});

const updateUndoSummary = (
  summary: UndoSerialsSummary,
  result: SerialProcessResult
): UndoSerialsSummary => {
  const updatedSummary = {
    ...summary,
    processed: summary.processed + 1,
  };

  switch (result.status) {
    case "undo_restored":
      updatedSummary.success += 1;
      updatedSummary.restored += 1;
      updatedSummary.removedPoints += result.points;
      break;

    case "undo_removed":
      updatedSummary.success += 1;
      updatedSummary.removed += 1;
      updatedSummary.removedPoints += result.points;
      break;

    case "undo_used_not_found":
      updatedSummary.usedNotFound += 1;
      break;

    case "undo_wrong_user":
      updatedSummary.wrongUser += 1;
      break;

    case "undo_user_serial_not_found":
      updatedSummary.userSerialNotFound += 1;
      break;

    case "undo_source_conflict":
      updatedSummary.sourceConflict += 1;
      break;

    case "failed":
    case "invalid":
      updatedSummary.failed += 1;
      break;
  }

  return updatedSummary;
};

const undoSingleSerial = async (
  userId: string,
  row: ExcelSerialRow
): Promise<SerialProcessResult> => {
  const serialNumber = normalizeSerialNumber(
    row.serialNumber
  );

  if (!serialNumber) {
    return {
      serialNumber: "",
      productId: normalizeText(row.productId),
      points: 0,
      rowNumber: row.rowNumber,
      status: "invalid",
      message: "رقم السيريال غير موجود",
    };
  }

  if (serialNumber.includes("/")) {
    return {
      serialNumber,
      productId: normalizeText(row.productId),
      points: 0,
      rowNumber: row.rowNumber,
      status: "invalid",
      message:
        "رقم السيريال يحتوي على / ولا يمكن استخدامه كمعرف دكيومنت",
    };
  }

  try {
    const transactionResult = await runTransaction(
      db,
      async (transaction) => {
        const userRef = doc(
          db,
          USERS_COLLECTION,
          userId
        );

        const usedSerialRef = doc(
          db,
          USED_SERIALS_COLLECTION,
          serialNumber
        );

        /*
         * نبدأ بالقراءات التي نحتاجها لمعرفة
         * مكان السيريال وبياناته الحقيقية.
         */
        const userSnapshot =
          await transaction.get(userRef);

        const usedSerialSnapshot =
          await transaction.get(usedSerialRef);

        if (!userSnapshot.exists()) {
          throw new Error("USER_NOT_FOUND");
        }

        /*
         * لا يمكن عكس العملية إذا لم يكن السيريال
         * موجودًا في UsedSerialNumbers.
         */
        if (!usedSerialSnapshot.exists()) {
          return {
            status: "undo_used_not_found" as const,
            message:
              "السيريال غير موجود داخل UsedSerialNumbers",
            productId: normalizeText(row.productId),
            points: 0,
          };
        }

        const usedData =
          usedSerialSnapshot.data();

        /*
         * حماية مهمة جدًا:
         * لا نسمح بحذف سيريال يعود لمستخدم آخر.
         */
        if (
          normalizeText(usedData.userId) !== userId
        ) {
          return {
            status: "undo_wrong_user" as const,
            message:
              "السيريال موجود في UsedSerialNumbers ولكنه يعود لمستخدم آخر",
            productId: normalizeText(
              usedData.productId
            ),
            points:
              normalizePoints(usedData.points) || 0,
          };
        }

        /*
         * حماية إضافية:
         * عملية التراجع مخصصة للسيريلات التي
         * أضيفت من هذه الأداة.
         */
        if (
          normalizeText(usedData.source) &&
          normalizeText(usedData.source) !==
            "dashboard_excel"
        ) {
          return {
            status: "undo_wrong_user" as const,
            message:
              "السيريال لم تتم إضافته بواسطة أداة dashboard_excel ولذلك لن يتم حذفه",
            productId: normalizeText(
              usedData.productId
            ),
            points:
              normalizePoints(usedData.points) || 0,
          };
        }

        const productId =
          normalizeText(usedData.productId);

        const points =
          normalizePoints(usedData.points);

        if (!productId) {
          throw new Error(
            "USED_SERIAL_PRODUCT_ID_MISSING"
          );
        }

        if (
          !Number.isFinite(points) ||
          points <= 0
        ) {
          throw new Error(
            "USED_SERIAL_POINTS_INVALID"
          );
        }

        const userSerialRef = doc(
          db,
          USERS_COLLECTION,
          userId,
          "myPoints",
          productId,
          "myserail",
          serialNumber
        );

        const sourceSerialRef = doc(
          db,
          ACTIVE_SERIALS_COLLECTION,
          serialNumber
        );

        /*
         * ما زلنا داخل مرحلة القراءة.
         */
        const userSerialSnapshot =
          await transaction.get(userSerialRef);

        const sourceSerialSnapshot =
          await transaction.get(sourceSerialRef);

        if (!userSerialSnapshot.exists()) {
          return {
            status:
              "undo_user_serial_not_found" as const,
            message:
              "السيريال موجود في UsedSerialNumbers لكنه غير موجود داخل نقاط هذا المستخدم",
            productId,
            points,
          };
        }

        const existedInSerailNumber =
          usedData.existedInSerailNumber === true;

        /*
         * إذا كان السيريال موجودًا أصلًا في
         * SerailNumber ثم وجدناه الآن موجودًا هناك،
         * لا نكتب فوقه حتى لا نتلف بيانات جديدة.
         */
        if (
          existedInSerailNumber &&
          sourceSerialSnapshot.exists()
        ) {
          return {
            status:
              "undo_source_conflict" as const,
            message:
              "تعذر الإرجاع لأن السيريال موجود حاليًا داخل SerailNumber",
            productId,
            points,
          };
        }

        const currentUserPoints =
          Number(userSnapshot.data().points) || 0;

        /*
         * لا نسمح بأن تصبح نقاط المستخدم سالبة.
         * وجود هذه الحالة يعني أن البيانات تغيرت
         * بعد عملية الإضافة ويجب مراجعتها يدويًا.
         */
        if (currentUserPoints < points) {
          throw new Error(
            "USER_POINTS_LESS_THAN_SERIAL_POINTS"
          );
        }

        const updatedUserPoints =
          currentUserPoints - points;

        /*
         * إذا كان السيريال قد جاء أصلًا من
         * SerailNumber، نعيده إليه.
         */
        if (existedInSerailNumber) {
          const originalData =
            usedData.originalData &&
            typeof usedData.originalData === "object"
              ? usedData.originalData
              : {};

          transaction.set(
            sourceSerialRef,
            originalData
          );
        }

        /*
         * حذف السيريال من المستخدم.
         */
        transaction.delete(userSerialRef);

        /*
         * حذف السيريال من القائمة المستخدمة.
         */
        transaction.delete(usedSerialRef);

        /*
         * خصم النقاط التي أضافتها العملية الأصلية.
         */
        transaction.update(userRef, {
          points: updatedUserPoints,
          pointsUpdatedAt: serverTimestamp(),
        });

        return {
          status: existedInSerailNumber
            ? ("undo_restored" as const)
            : ("undo_removed" as const),

          message: existedInSerailNumber
            ? "تم حذف السيريال من المستخدم وخصم نقاطه وإعادته إلى SerailNumber"
            : "تم حذف السيريال من المستخدم وUsedSerialNumbers وخصم نقاطه",

          productId,
          points,
        };
      }
    );

    return {
      serialNumber,
      productId:
        transactionResult.productId ||
        normalizeText(row.productId),

      points:
        Number(transactionResult.points) || 0,

      rowNumber: row.rowNumber,

      status: transactionResult.status,

      message: transactionResult.message,
    };
  } catch (error) {
    console.error(
      `خطأ أثناء عكس السيريال ${serialNumber}:`,
      error
    );

    let message =
      error instanceof Error
        ? error.message
        : "حدث خطأ غير معروف";

    switch (message) {
      case "USER_NOT_FOUND":
        message = "المستخدم غير موجود";
        break;

      case "USED_SERIAL_PRODUCT_ID_MISSING":
        message =
          "UsedSerialNumbers لا يحتوي على productId لهذا السيريال";
        break;

      case "USED_SERIAL_POINTS_INVALID":
        message =
          "قيمة points داخل UsedSerialNumbers غير صحيحة";
        break;

      case "USER_POINTS_LESS_THAN_SERIAL_POINTS":
        message =
          "نقاط المستخدم الحالية أقل من نقاط السيريال، لذلك تم إيقاف التراجع لحماية البيانات";
        break;
    }

    return {
      serialNumber,
      productId: normalizeText(row.productId),
      points: 0,
      rowNumber: row.rowNumber,
      status: "failed",
      message,
    };
  }
};

export const undoSerialsFromUser = async (
  userId: string,
  rows: ExcelSerialRow[],
  options: UndoSerialsOptions = {}
): Promise<{
  results: SerialProcessResult[];
  summary: UndoSerialsSummary;
}> => {
  const normalizedUserId =
    normalizeText(userId);

  if (!normalizedUserId) {
    throw new Error(
      "يرجى إدخال معرف المستخدم"
    );
  }

  if (!rows.length) {
    throw new Error(
      "لا توجد سيريلات للمعالجة"
    );
  }

  const userExists =
    await checkUserExists(normalizedUserId);

  if (!userExists) {
    throw new Error(
      "معرف المستخدم غير موجود داخل كولكشن users"
    );
  }

  const results: SerialProcessResult[] = [];

  let summary: UndoSerialsSummary = {
    ...createEmptyUndoSummary(),
    total: rows.length,
  };

  /*
   * متسلسلة لأن كل Transaction تعدل points
   * لنفس المستخدم.
   */
  for (
    let index = 0;
    index < rows.length;
    index++
  ) {
    const row = rows[index];

    const result = await undoSingleSerial(
      normalizedUserId,
      row
    );

    results.push(result);

    summary = updateUndoSummary(
      summary,
      result
    );

    options.onProgress?.(
      index + 1,
      rows.length,
      result
    );
  }

  return {
    results,
    summary,
  };
};